import { formValueSelector } from 'redux-form';
import { createSelector } from 'reselect';

import { Forms } from '../../form/form.selector';
import { RootState } from '../../reducers';
import { Mixer, MixerStatus } from './mixers.types';
import { CodeElement, CodeTypeNames } from '../code/code.types';
import { getCodeListFilteredByType } from '../code/code.selector';

const mixersFilterForm = formValueSelector(Forms.mixersFilterForm);
const mixerCreationForm = formValueSelector(Forms.mixerCreationForm);

const mixerNameSelector = (state: RootState) => mixersFilterForm(state, 'mixerName') || '';
export const serviceSelector = (state: RootState) => (serviceFieldName: string) =>
  mixerCreationForm(state, serviceFieldName) || '';
export const getMixersList = (state: RootState): Mixer[] => state.entities.mixers.mixersList;
export const getMixerStatus = (state: RootState): MixerStatus => state.entities.mixers.mixerStatus;

export const getFilteredMixersList = createSelector([getMixersList, mixerNameSelector], (mixerList, mixerName) =>
  mixerList.filter(
    (mixer) =>
      mixer.displayName.toLowerCase().includes(mixerName.toLowerCase()) ||
      mixer.description.toLowerCase().includes(mixerName.toLowerCase()),
  ),
);

export const noMixers = createSelector(
  getMixersList,
  (mixerList) => mixerList.length === 1 && mixerList[0].loadSize === 0 && mixerList[0].scaleNumber === 1,
);

export const getMixersByZoneId = createSelector(
  [getMixersList, (state: RootState, id: string) => id],
  (mixerList, id) => mixerList.filter((elem) => elem.zoneIdList.find((zoneId) => zoneId === id)),
);

export const hasFilters = createSelector([mixerNameSelector], (mixerName): boolean => !!mixerName);

export const hasMixerWithExternalDatalink = createSelector([getMixersList], (mixers) =>
  mixers.some((mixer) => mixer.useDatalink),
);

const getMixcerRoleCodesList = (state: RootState): CodeElement[] =>
  getCodeListFilteredByType(state, { codeType: CodeTypeNames.mixerRoleCodes });

export const getMixerRoleCode = createSelector([getMixcerRoleCodesList, (state, code: number) => code], (codes, code) =>
  codes.find((elem) => elem.code === code),
);

export const isWeightMotionToleranceSupported = (state: RootState) => {
  const mixers = state.entities.mixers.mixersList;
  return mixers.some((mixer) => mixer.supportsTolerance);
};

export const addToBaseSupports = (state: RootState) => {
  const mixers = getMixersList(state);
  return mixers.every((x) => x.supportsRationType2);
};

export const refusalSubstitutionSupports = (state: RootState) => {
  const mixers = getMixersList(state);
  return mixers.every((x) => x.supportsRationType3);
};

export const firstIngredientResizeSupports = (state: RootState) => {
  const mixers = getMixersList(state);
  return mixers.every((x) => x.supportsRationType4);
};

export const canDeleteMixer = (state: RootState) => {
  const mixers = getMixersList(state);
  return mixers.length > 1;
};
